import type { Schedule } from '../storage/schemas';
import { nextOccurrence, previousOccurrence } from './recurrence';

export interface ActiveResolution {
  noteId: string | null;
  /** The schedule that put this note on screen, if any. */
  scheduleId: string | null;
  /** When the scheduled window ends (null = until the next switch or never). */
  until: number | null;
  source: 'schedule' | 'fallback' | 'manual';
}

interface SwitchEvent {
  schedule: Schedule;
  start: number;
}

function latestSwitchAtOrBefore(at: number, switches: Schedule[]): SwitchEvent | null {
  let best: SwitchEvent | null = null;
  for (const s of switches) {
    const start = previousOccurrence(s.rule, at);
    if (start === null) continue;
    // Ties: the schedule created later wins (documented in validation warnings).
    if (!best || start > best.start || (start === best.start && s.createdAt > best.schedule.createdAt)) best = { schedule: s, start };
  }
  return best;
}

/**
 * Decide which note is "on the lock screen" at `now`.
 * The most recent switch occurrence wins while its window is open. When a window with a duration closes,
 * the schedule's fallback applies: `previous` = whatever was active just before that switch, `note` = a fixed note.
 */
export function resolveActiveNote(now: number, schedules: Schedule[], manualActiveId: string | null, noteExists: (id: string) => boolean, depth = 0): ActiveResolution {
  const switches = schedules.filter((s) => s.enabled && s.switchNote && noteExists(s.noteId));
  const manual: ActiveResolution = { noteId: manualActiveId, scheduleId: null, until: null, source: 'manual' };
  if (!switches.length || depth > 6) return manual;
  const ev = latestSwitchAtOrBefore(now, switches);
  if (!ev) return manual;
  const s = ev.schedule;
  if (s.durationMinutes === null) {
    const nexts = switches.map((o) => nextOccurrence(o.rule, now)).filter((t): t is number => t !== null);
    return { noteId: s.noteId, scheduleId: s.id, until: nexts.length ? Math.min(...nexts) : null, source: 'schedule' };
  }
  const end = ev.start + s.durationMinutes * 60_000;
  if (now < end) return { noteId: s.noteId, scheduleId: s.id, until: end, source: 'schedule' };
  if (s.fallback.kind === 'note' && s.fallback.noteId && noteExists(s.fallback.noteId)) {
    return { noteId: s.fallback.noteId, scheduleId: s.id, until: null, source: 'fallback' };
  }
  const before = resolveActiveNote(ev.start - 1, schedules, manualActiveId, noteExists, depth + 1);
  // A previous window that would itself have ended by now does not come back.
  if (before.source === 'schedule' && before.until !== null && before.until <= now && before.until !== ev.start) return { ...manual, source: 'fallback' };
  return { ...before, until: null, source: before.source === 'manual' ? 'fallback' : before.source };
}
